import { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast'; // PrimeReact Toast
import apiClient from '../services/axiosInterceptor';

export interface SubjectOption {
  label: string;
  value: string;
  id: string;
  subjectName: string;
  classId: string;
}

interface SubjectResponse {
  subject_id: number;
  subject_name: string;
  class_id: number;
  class_name?: string;
  created_at: string;
  updated_at: string;
}

interface SubjectsApiResponse {
  status: 'success' | 'error';
  subjects: SubjectResponse[];
  message?: string;
}

export const useSubjectsByClass = (classId?: string | null, includePlaceholder: boolean = false) => {
  const [subjectOptions, setSubjectOptions] = useState<SubjectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useRef<Toast>(null); // PrimeReact Toast ref

  useEffect(() => {
    const fetchSubjects = async () => {
      if (!classId) {
        setSubjectOptions(
          includePlaceholder
            ? [{ label: 'Select a Subject', value: '', id: '', subjectName: '', classId: '' }]
            : []
        );
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await apiClient.get<SubjectsApiResponse>(
          `subjects_by_class?class_id=${classId}`
        );

        if (response.data.status === 'success') {
          if (response.data.subjects.length === 0) {
            setSubjectOptions(
              includePlaceholder
                ? [{ label: 'No subjects available', value: '', id: '', subjectName: '', classId: '' }]
                : []
            );
            setError('No subjects available for this class');
            toast.current?.show({
              severity: 'warn',
              summary: 'Warning',
              detail: 'No subjects available for this class',
              life: 3000,
            });
          } else {
            const options: SubjectOption[] = response.data.subjects
              .sort((a, b) => a.subject_name.localeCompare(b.subject_name))
              .map((sub) => ({
                label: sub.subject_name,
                value: sub.subject_id.toString(),
                id: sub.subject_id.toString(),
                subjectName: sub.subject_name,
                classId: sub.class_id.toString(),
              }));

            setSubjectOptions(
              includePlaceholder
                ? [
                    { label: 'Select a Subject', value: '', id: '', subjectName: '', classId: '' },
                    ...options,
                  ]
                : options
            );
          }
        } else {
          setError(response.data.message || 'Failed to load subjects.');
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: response.data.message || 'Failed to load subjects.',
            life: 3000,
          });
        }
      } catch (err: any) {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          'An error occurred while fetching subjects.';
        setError(message);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: message,
          life: 3000,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSubjects();
  }, [classId, includePlaceholder]);

  return {
    subjectOptions,
    loading,
    error,
    toast, // Expose toast ref if needed for parent components
  };
};
